import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import ReactQuill from 'react-quill';
import {
  Button,
  Label,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import { toast } from 'react-toastify';
import { useMutation } from '@tanstack/react-query';
import { yupResolver } from '@hookform/resolvers/yup';
import { initialValues, validationSchema } from './constant';
import ratingIcon from '../../../../assets/images/performance/ratingIcon.png';
import starIcon from '../../../../assets/images/performance/starIcon.svg';
import { selfRating } from '../../../../store/apis/performanceService';

function SelfRating({
  edit,
  selectedKPI,
  refetchPerformanceDetails,
  selectedEmployee,
}) {
  const [modalRating, setModalRating] = useState(false);

  const {
    handleSubmit,
    reset,
    control,
    setValue,
    formState: { errors, isValid },
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(validationSchema),
    defaultValues: initialValues,
  });

  useEffect(() => {
    reset();
    if (selectedKPI?.Rating_Self) {
      setValue('rating', selectedKPI.Rating_Self);
      setValue('comment', selectedKPI.Rating_Self_Comments);
    }
  }, [reset, selectedKPI, setValue]);

  const { mutate: addRating, isPending } = useMutation({
    mutationFn: selfRating,
    onSuccess: () => {
      toast.success('Rating saved successfully!', {
        className: 'toast-success',
      });
      refetchPerformanceDetails();
    },
    onError: error => {
      toast.error(error?.message || 'Failed to save rating', {
        className: 'toast-error',
      });
    },
  });

  const toggleRating = e => {
    e?.stopPropagation();
    setModalRating(!modalRating);
  };

  const onSubmit = data => {
    const payload = {
      kpiCategory: selectedKPI?.KPI_Category,
      kpiSeq: selectedKPI?.KPI_Sequence,
      ratingSelf: data.rating,
      ratingSelfComments: data.comment,
      employeeId: selectedEmployee?.value,
      userId: '',
    };
    addRating(payload);
    toggleRating();
  };

  return (
    <div>
      <Button
        className="btn-primary primary actionItem fs-6 btn"
        onClick={toggleRating}
        style={{ height: '28px' }}
      >
        <img src={starIcon} alt="" height={14} />
        {edit ? 'Edit Rating' : 'Self Rating'}
      </Button>
      <Modal
        isOpen={modalRating}
        toggle={toggleRating}
        centered
        backdrop="static"
        keyboard={false}
      >
        <ModalHeader toggle={toggleRating}>
          <div className="addKpiHeader">
            <img alt="Rating Icon" src={ratingIcon} />
            <div className="title">{edit ? 'Edit Rating' : 'Self Rating'}</div>
          </div>
        </ModalHeader>
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalBody>
            <div className="modalRatingBody">
              <div className="ratingInput">
                <Label>
                  Rating
                  <span className="text-danger"> *</span>
                </Label>
                <Controller
                  name="rating"
                  control={control}
                  render={({ field }) => (
                    <Input
                      id="exampleSelect"
                      type="select"
                      className="external-filter-input "
                      {...field}
                      title="Enter Self Rating."
                    >
                      <option value="">Select</option>
                      <option value="1">1 - Unsatisfactory</option>
                      <option value="2">2 - Need Improvement</option>
                      <option value="3">3 - Well Done</option>
                      <option value="4">4 - Above Expectation</option>
                      <option value="5">5 - Exceptional</option>
                    </Input>
                  )}
                />
                {errors.rating && (
                  <span className="text-danger">{errors.rating.message}</span>
                )}
              </div>
              <div className="ratingInput">
                <Label>
                  Comment
                  <span className="text-danger"> *</span>
                </Label>
                <Controller
                  name="comment"
                  control={control}
                  render={({ field }) => (
                    <ReactQuill
                      {...field}
                      placeholder="Enter"
                      title="Enter self rating comments"
                    />
                  )}
                />
                {errors.comment && (
                  <span className="text-danger">{errors.comment.message}</span>
                )}
              </div>
            </div>
          </ModalBody>
          <ModalFooter className="modalFooter">
            <Button
              className="item custom-light-btn"
              onClick={toggleRating}
              type="button"
            >
              Cancel
            </Button>
            <Button
              className="item custom-btn"
              type="submit"
              disabled={!isValid || isPending}
            >
              {isPending ? 'Saving...' : 'Save'}
            </Button>
          </ModalFooter>
        </form>
      </Modal>
    </div>
  );
}

export default SelfRating;
